import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import {
  ArrowUpRight, CalendarDays, ChevronDown, ChevronLeft, ChevronRight,
  CircleHelp, Gauge, Lightbulb, Loader2, LogOut, Menu, Plus, Sparkles, WalletCards,
} from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import CategoryModal from "@/components/CategoryModal";
import BudgetModal from "@/components/BudgetModal";
import TransactionModal from "@/components/TransactionModal";
import {
  useTransactionSummary, useTransactions, useFinancialHealth,
  useRoast, useRoastHistory, useGenerateRoast,
  useBudgetHealth, useInsights,
} from "@/hooks/useApi";
import { formatRupiah, getWeekStart, formatWeekRange, toYMD, getCategoryEmoji, getTone, renderPeppers, healthLabel } from "@/lib/format";

/* ─── Protected Route ─── */
function ProtectedRoute({ children }) {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
}

/* ─── Skeleton card ─── */
function SkeletonCard() {
  return (
    <div style={{ height: "8px", background: "#eeeae5", borderRadius: 2, marginBottom: 8 }} />
  );
}

/* ─── Main Dashboard ─── */
function Home() {
  const { user, logout, isAuthenticated, updateSpicePreference } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [modal, setModal] = useState(null); // 'transaction' | 'category' | 'budget' | null
  const [cooldownMsg, setCooldownMsg] = useState("");
  const [spiceToast, setSpiceToast] = useState("");

  // ── Week navigation state
  const [selectedWeekStart, setSelectedWeekStart] = useState(() => getWeekStart());

  const weekStartStr = toYMD(selectedWeekStart);
  const weekEndStr = toYMD(new Date(selectedWeekStart.getTime() + 6 * 86400000));

  const prevWeek = () => setSelectedWeekStart(d => {
    const n = new Date(d); n.setDate(n.getDate() - 7); return n;
  });
  const nextWeek = () => setSelectedWeekStart(d => {
    const n = new Date(d); n.setDate(n.getDate() + 7); return n;
  });
  const isCurrentWeek = toYMD(selectedWeekStart) === toYMD(getWeekStart());

  // ── Week filter for Category Breakdown
  const [selectedWeekFilter, setSelectedWeekFilter] = useState("all");

  // ── API data
  const { data: summary, isLoading: summaryLoading } = useTransactionSummary("month");
  const { data: financialHealth, isLoading: financialHealthLoading } = useFinancialHealth("month");
  const { data: health, isLoading: healthLoading } = useBudgetHealth();
  const { data: roast, isLoading: roastLoading, error: roastError } = useRoast(weekStartStr);
  const { data: insights = [], isLoading: insightsLoading } = useInsights(weekStartStr);
  const { data: roastHistory = [] } = useRoastHistory();
  const generateRoast = useGenerateRoast();

  // ── Category breakdown grouped by week
  const weeklyBreakdown = (summary?.weekly_breakdown || []).map(w => ({
    ...w,
    formattedRange: formatWeekRange(w.week_start),
    categories: (w.categories || []).map(c => ({
      id: c.category_id,
      name: c.category_name,
      total: c.total_amount ?? c.amount ?? 0,
      percent: c.pct_of_total ?? 0,
      tone: getTone(c.pct_of_total ?? 0),
      emoji: getCategoryEmoji(c.category_name),
    }))
  }));

  const filteredWeeks = selectedWeekFilter === "all"
    ? weeklyBreakdown
    : weeklyBreakdown.filter(w => w.week_start === selectedWeekFilter);

  // ── Generate roast handler
  const handleGenerate = async (force = false) => {
    setCooldownMsg("");
    try {
      await generateRoast.mutateAsync({ force });
    } catch (e) {
      if (e.response?.status === 429) {
        setCooldownMsg(e.response.data?.error || "Masih dalam cooldown 24 jam.");
      } else {
        setCooldownMsg("Generate gagal. Coba lagi nanti.");
      }
    }
  };

  const displayTotalExpense = summary?.expense?.total_expense ?? summary?.totalExpense ?? 0;
  const displayTotalIncome = summary?.income?.total_income ?? summary?.totalIncome ?? 0;
  const displayAvgPerDay = summary?.avgExpensePerDay ?? (displayTotalExpense > 0 ? displayTotalExpense / 7 : 0);
  const expenseIncomeRatioPct = displayTotalIncome > 0
    ? Math.min(100, Math.round((displayTotalExpense / displayTotalIncome) * 100))
    : 0;

  const healthScore = health?.healthScore ?? null;
  const healthTrackColor = healthScore >= 80 ? "#4caf50" : healthScore >= 60 ? "#e4ae32" : "#df2929";

  const avatarLetters = user?.username ? user.username.slice(0, 2).toUpperCase() : "FR";

  return (
    <div className="app-shell">
      {/* Modals */}
      {modal === "category" && <CategoryModal onClose={() => setModal(null)} />}
      {modal === "budget" && <BudgetModal onClose={() => setModal(null)} />}
      {modal === "transaction" && <TransactionModal onClose={() => setModal(null)} />}

      <nav className="topbar" data-testid="main-navigation">
        <div className="brand" data-testid="brand-logo">
          <span className="brand-mark">$</span>
          <span>FINANCIAL<br /><b>ROAST</b></span>
        </div>
        <div className="nav-links">
          <a href="#dashboard" data-testid="dashboard-nav-link">Dashboard</a>
          <a href="#insights" data-testid="insights-nav-link">Insights</a>
          <a href="#history" data-testid="history-nav-link">Roast history</a>
        </div>
        <div className="nav-right">
          <span className="status-dot" data-testid="connection-status">● LIVE</span>
          {isAuthenticated ? (
            <>
              {/* Action buttons — visible until 480px, then masuk hamburger */}
              <div className="nav-actions" style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <button className="primary-button" onClick={() => setModal("transaction")}
                  style={{ fontSize: "7px", padding: "8px 10px", gap: "6px" }}>
                  <Plus size={13} /> TRANSAKSI
                </button>
                <button className="primary-button" onClick={() => setModal("category")}
                  style={{ fontSize: "7px", padding: "8px 10px", gap: "6px" }}>
                  <Plus size={13} /> KATEGORI
                </button>
                <button className="icon-button" onClick={() => { setMenuOpen(false); logout(); }} title="Logout">
                  <LogOut size={18} />
                </button>
              </div>
              <button className="icon-button mobile-menu" aria-label="Open menu" aria-expanded={menuOpen}
                onClick={() => setMenuOpen(o => !o)} data-testid="mobile-menu-button">
                <Menu size={20} />
              </button>
              <div className="avatar" data-testid="profile-avatar" title={user?.email}>{avatarLetters}</div>
            </>
          ) : (
            <a href="/login" className="primary-button" style={{ fontSize: "8px", padding: "8px 14px", textDecoration: "none", gap: "6px" }}>
              <Sparkles size={13} /> MASUK / DAFTAR
            </a>
          )}
        </div>
        {isAuthenticated && menuOpen && (
          <div className="mobile-nav-panel" data-testid="mobile-navigation-panel">
            <a href="#dashboard" onClick={() => setMenuOpen(false)}>Dashboard</a>
            <a href="#insights" onClick={() => setMenuOpen(false)}>Insights</a>
            <a href="#history" onClick={() => setMenuOpen(false)}>Roast history</a>
            <div style={{ padding: "10px 14px", borderBottom: "1px solid #e8e5e0", background: "#fbf9f4" }}>
              <span style={{ font: "8px 'Press Start 2P'", color: "var(--muted)", display: "block", marginBottom: "8px" }}>
                🌶️ TINGKAT PEDAS ROAST
              </span>
              <select
                value={user?.spice_preference || "sedang"}
                onChange={async (e) => {
                  const val = e.target.value;
                  const res = await updateSpicePreference(val);
                  if (res.ok) {
                    setSpiceToast(`Tingkat pedas diubah ke ${val.toUpperCase()} 🌶️`);
                    setTimeout(() => setSpiceToast(""), 3000);
                  }
                }}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  font: "700 12px 'Space Grotesk'",
                  border: "2px solid var(--line)",
                  background: "white",
                  boxShadow: "2px 2px 0 var(--line)"
                }}
              >
                <option value="mild">🟢 Mild — Santai aja</option>
                <option value="sedang">🟡 Sedang — Lumayan pedes</option>
                <option value="pedas">🔴 Pedas — Siap digituin</option>
              </select>
            </div>
            <button onClick={() => { setModal("transaction"); setMenuOpen(false); }}
              style={{ padding: "10px", textAlign: "left", border: "none", background: "none", cursor: "pointer", fontWeight: 700, fontSize: "12px" }}>
              + Catat Transaksi
            </button>
            <button onClick={() => { setModal("category"); setMenuOpen(false); }}
              style={{ padding: "10px", textAlign: "left", border: "none", background: "none", cursor: "pointer", fontWeight: 700, fontSize: "12px" }}>
              ⚙ Kelola Kategori
            </button>
            <button onClick={() => { setModal("budget"); setMenuOpen(false); }}
              style={{ padding: "10px", textAlign: "left", border: "none", background: "none", cursor: "pointer", fontWeight: 700, fontSize: "12px" }}>
              📊 Kelola Budget
            </button>
            <button onClick={() => { setMenuOpen(false); logout(); }}
              style={{ padding: "10px", textAlign: "left", border: "none", background: "none", cursor: "pointer", fontWeight: 700, fontSize: "12px", color: "var(--red)" }}>
              ← Logout
            </button>
          </div>
        )}
      </nav>

      <main id="dashboard" className="page-wrap">
        <header className="hero reveal">
          <div>
            <div className="eyebrow"><span className="eyebrow-line" /> WEEKLY MONEY CHECK-IN</div>
            <h1>Uang lo pergi,<br /><em>kami komentari.</em></h1>
            <p className="hero-copy">
              Roast ringan dari pola spending mingguan lo.<br className="desktop-only" />
              {isAuthenticated ? "Pedasnya informatif, sakitnya produktif." : "Masuk atau buat akun untuk mulai pantau dompet & di-roast sama AI secara jujur (nyelekit sih tergantung level:D)."}
            </p>
          </div>
          <div className="hero-sticker" data-testid="hero-sticker"><span>NO<br />FILTER</span><Sparkles size={20} /></div>
        </header>

        {/* ── Stat cards ── */}
        <section className="stat-grid reveal delay-1" aria-label="Weekly spending summary">
          {/* Total Spending */}
          <article className="stat-card dark-card" data-testid="weekly-total-card">
            <span className="card-label light-label">TOTAL SPENDING <span className="period-chip">7 HARI</span></span>
            {summaryLoading ? (
              <><SkeletonCard /><SkeletonCard /></>
            ) : (
              <strong>{formatRupiah(displayTotalExpense)}</strong>
            )}
            <div className="stat-foot">
              {isAuthenticated ? (
                <span style={{ color: "#a2a1a5" }}>
                  Income Bulan Ini: {formatRupiah(displayTotalIncome)}
                </span>
              ) : (
                <span style={{ color: "#a2a1a5" }}>Masuk untuk sinkronisasi</span>
              )}
            </div>
            <div className="mini-bars" aria-hidden="true"><i /><i /><i /><i /><i /><i /><i /></div>
          </article>

          {/* Avg per day */}
          <article className="stat-card" data-testid="daily-average-card">
            <span className="card-label">RATA-RATA / HARI</span>
            {summaryLoading ? (
              <><SkeletonCard /><SkeletonCard /></>
            ) : (
              <strong>{formatRupiah(displayAvgPerDay)}</strong>
            )}
            <div className="stat-foot">
              <span style={{ color: "var(--muted)" }}>periode 7 hari terakhir</span>
            </div>
            <div className="target-line">
              <span style={{ width: `${expenseIncomeRatioPct}%` }} />
            </div>
          </article>

          {/* Budget Health */}
          <article className="stat-card" data-testid="budget-health-card">
            <div className="card-label-row">
              <span className="card-label">BUDGET HEALTH</span>
              <Gauge size={17} />
            </div>
            {healthLoading ? (
              <><SkeletonCard /><SkeletonCard /></>
            ) : !isAuthenticated || healthScore === null ? (
              <div>
                <div className="health-score"><strong>—</strong><span>/ 100</span><b>BELUM ADA BUDGET</b></div>
                {isAuthenticated ? (
                  <button className="text-button" style={{ marginTop: "10px" }} onClick={() => setModal("budget")}>
                    Atur budget <ArrowUpRight size={13} />
                  </button>
                ) : (
                  <a href="/login" className="text-button" style={{ marginTop: "10px", textDecoration: "none" }}>
                    Atur budget <ArrowUpRight size={13} />
                  </a>
                )}
              </div>
            ) : (
              <>
                <div className="health-score">
                  <strong>{healthScore}</strong>
                  <span>/ 100</span>
                  <b>{healthLabel(healthScore)}</b>
                </div>
                <div className="health-track">
                  <span style={{ width: `${healthScore}%`, background: healthTrackColor }} />
                </div>
                <div className="stat-foot">
                  <span>{Math.round(health.maxUsagePercentage)}% budget terpakai</span>
                  <button className="text-button" style={{ fontSize: "10px" }} onClick={() => setModal("budget")}>
                    Edit budget
                  </button>
                </div>
              </>
            )}
          </article>

          {/* Status Keuangan (Surplus / Defisit) */}
          <article className="stat-card" data-testid="financial-health-card">
            <div className="card-label-row">
              <span className="card-label">
                STATUS KEUANGAN{" "}
                <span style={{
                  color: financialHealth?.status === "surplus" ? "#2e7d32" : financialHealth?.status === "defisit" ? "var(--red)" : "#aa7c00",
                  fontFamily: "'Press Start 2P', monospace",
                  fontSize: "8px"
                }}>
                  ({financialHealth?.status?.toUpperCase() || "BALANCE"})
                </span>
              </span>
            </div>
            {financialHealthLoading ? (
              <><SkeletonCard /><SkeletonCard /></>
            ) : (
              <strong style={{
                color: financialHealth?.status === "surplus" ? "#2e7d32" : financialHealth?.status === "defisit" ? "var(--red)" : "inherit"
              }}>
                {formatRupiah(financialHealth?.balance ?? 0)}
              </strong>
            )}
            <div className="stat-foot">
              {isAuthenticated ? (
                <span>
                  {financialHealth?.saving_rate_pct !== null && financialHealth?.saving_rate_pct !== undefined
                    ? `Saving rate: ${financialHealth.saving_rate_pct}%`
                    : `Nabung: ${formatRupiah(financialHealth?.total_saving ?? 0)}`}
                </span>
              ) : (
                <span>Nabung: Rp 0</span>
              )}
            </div>
            <div className="target-line">
              <span style={{
                width: financialHealth ? `${Math.min(100, Math.max(10, Math.abs(financialHealth.balance) / (financialHealth.total_income || 1) * 100))}%` : "0%",
                background: financialHealth?.status === "surplus" ? "#4caf50" : financialHealth?.status === "defisit" ? "var(--red)" : "#e4ae32"
              }} />
            </div>
          </article>
        </section>

        {/* ── Roast Section ── */}
        <section className="roast-section reveal delay-2" id="history" data-testid="roast-section">
          <div className="section-heading">
            <div>
              <div className="eyebrow red-eyebrow"><span className="eyebrow-line" /> AI ROAST OF THE WEEK</div>
              <h2>Roast lo minggu ini</h2>
            </div>
            {/* Controls: Spice Selector + Week Navigator */}
            <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
              {isAuthenticated && (
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <select
                    value={user?.spice_preference || "sedang"}
                    onChange={async (e) => {
                      const val = e.target.value;
                      const res = await updateSpicePreference(val);
                      if (res.ok) {
                        setSpiceToast(`Tingkat pedas diubah ke ${val.toUpperCase()} 🌶️`);
                        setTimeout(() => setSpiceToast(""), 3000);
                      }
                    }}
                    style={{
                      font: "700 11px 'Space Grotesk'",
                      padding: "8px 12px",
                      border: "2px solid var(--line)",
                      background: "white",
                      boxShadow: "2px 2px 0 var(--line)",
                      cursor: "pointer",
                      outline: "none"
                    }}
                    title="Ubah tingkat kepedasan roast AI"
                  >
                    <option value="mild">🟢 Mild — Santai aja</option>
                    <option value="sedang">🟡 Sedang — Lumayan pedes</option>
                    <option value="pedas">🔴 Pedas — Siap digituin</option>
                  </select>
                </div>
              )}

              {/* Week navigator */}
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <button onClick={prevWeek}
                  style={{ border: "2px solid var(--line)", background: "white", padding: "6px 8px", cursor: "pointer" }}>
                  <ChevronLeft size={14} />
                </button>
                <div className="date-select" data-testid="week-selector" style={{ padding: "9px 14px" }}>
                  <CalendarDays size={15} />
                  {formatWeekRange(weekStartStr)}
                </div>
                <button onClick={nextWeek} disabled={isCurrentWeek}
                  style={{ border: "2px solid var(--line)", background: isCurrentWeek ? "#eee" : "white", padding: "6px 8px", cursor: isCurrentWeek ? "not-allowed" : "pointer" }}>
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </div>

          {/* Spice level updated toast */}
          {spiceToast && (
            <div style={{
              background: "#e8f5e9", border: "1px solid #a5d6a7", padding: "10px 16px",
              marginBottom: "14px", fontSize: "11px", fontWeight: 700, color: "#2e7d32",
              display: "flex", alignItems: "center", gap: "8px"
            }}>
              <span>✅</span>
              <span>{spiceToast}</span>
            </div>
          )}

          {/* Cooldown / error message */}
          {cooldownMsg && (
            <div style={{
              background: "#fff6d7", border: "1px solid #edd78a", padding: "12px 16px",
              marginBottom: "16px", fontSize: "12px", fontWeight: 700, color: "#7a5c00",
              display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "10px"
            }}>
              <div>⏳ {cooldownMsg}</div>
              <button
                onClick={() => handleGenerate(true)}
                disabled={generateRoast.isPending}
                style={{
                  font: "8px 'Press Start 2P'", padding: "6px 10px",
                  background: "var(--ink)", color: "white", border: "2px solid var(--line)",
                  boxShadow: "2px 2px 0 var(--line)", cursor: "pointer"
                }}
              >
                {generateRoast.isPending ? "MENGULIK..." : "⚡ RE-GENERATE LAGI (DEV)"}
              </button>
            </div>
          )}

          {/* Roast card */}
          {!isAuthenticated ? (
            <article className="roast-card" data-testid="main-roast-card" style={{ background: "var(--ink)", color: "white" }}>
              <div className="roast-meta">
                <span className="roast-badge" style={{ background: "var(--red)" }}>PERLU LOGIN 🔒</span>
                <span className="roast-time">{formatWeekRange(weekStartStr)}</span>
              </div>
              <div className="roast-body" style={{ gridTemplateColumns: "1fr", padding: "34px 0 26px" }}>
                <h3 style={{ font: "700 24px 'Space Grotesk'", color: "#ffcf5d", margin: "0 0 10px", letterSpacing: "-0.5px" }}>
                  Mau Dompet Lo Di-Roast Sama AI? 🌶️
                </h3>
                <p className="roast-text" style={{ fontSize: "16px", lineHeight: "1.5", opacity: 0.9, maxWidth: "680px", margin: 0 }}>
                  Masuk atau buat akun dulu untuk mulai mencatat transaksi. Setiap minggu, AI bakal kasih ulasan sarkastik tapi membangun soal kebiasaan spending lo!
                </p>
              </div>
              <div className="roast-actions">
                <span className="generated-note">Gratis & langsung siap dipakai</span>
                <a href="/login" className="primary-button" style={{ textDecoration: "none" }}>
                  <Sparkles size={16} /> MASUK / DAFTAR DULU
                </a>
              </div>
            </article>
          ) : roastLoading ? (
            <div className="roast-card" style={{ minHeight: "200px", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" }}>
              <Loader2 className="spin" size={20} />
              <span style={{ font: "9px 'Press Start 2P'", opacity: 0.8 }}>MEMUAT ROAST...</span>
            </div>
          ) : roastError || !roast ? (
            <article className="roast-card" data-testid="main-roast-card">
              <div className="roast-meta">
                <span className="roast-badge">ROAST NOT FOUND</span>
                <span className="roast-time">{formatWeekRange(weekStartStr)}</span>
              </div>
              <div className="roast-body" style={{ gridTemplateColumns: "1fr", padding: "36px 0 28px" }}>
                <p className="roast-text" style={{ fontSize: "clamp(20px,2.5vw,32px)" }}>
                  Belum ada roast untuk minggu ini.{" "}
                  <span>Klik generate untuk mulai diroast! 🌶️</span>
                </p>
              </div>
              <div className="roast-actions">
                <span className="generated-note">Generate roast AI berdasarkan transaksi minggu ini.</span>
                {isCurrentWeek && (
                  <button className="primary-button" onClick={handleGenerate} disabled={generateRoast.isPending} data-testid="generate-roast-button">
                    {generateRoast.isPending ? <><Loader2 className="spin" size={16} /> MENGULIK DOMPET...</> : <><Sparkles size={16} /> GENERATE ROAST BARU</>}
                  </button>
                )}
              </div>
            </article>
          ) : (
            <article className="roast-card" data-testid="main-roast-card">
              <div className="roast-meta">
                <span className="roast-badge">ROAST #{String(roast.id).padStart(3, "0")}</span>
                <span className="roast-time">{new Date(roast.updated_at).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" })} · ANALISIS 7 HARI</span>
              </div>
              <div className="roast-body">
                <div className="quote-mark">"</div>
                <div>
                  <p className="roast-text">{roast.roast_text}</p>
                </div>
                <div className="roast-score">
                  <span>LEVEL PEDAS</span>
                  <strong>{roast.spice_level}<span>/10</span></strong>
                  <div className="pepper-row">{renderPeppers(roast.spice_level)}</div>
                  {isAuthenticated && (
                    <span style={{ fontSize: "9px", opacity: 0.85, marginTop: "6px", display: "block" }}>
                      Mode: {user?.spice_preference === "mild" ? "🟢 Mild" : user?.spice_preference === "pedas" ? "🔴 Pedas" : "🟡 Sedang"}
                    </span>
                  )}
                </div>
              </div>
              <div className="roast-actions">
                <span className="generated-note" data-testid="roast-status">
                  {generateRoast.isSuccess ? "Roast baru siap dibaca." : "Berdasarkan pola transaksi lo"}
                </span>
                {isCurrentWeek && (
                  <button className="primary-button" onClick={handleGenerate} disabled={generateRoast.isPending} data-testid="generate-roast-button">
                    {generateRoast.isPending ? <><Loader2 className="spin" size={16} /> MENGULIK DOMPET...</> : <><Sparkles size={16} /> GENERATE ROAST BARU</>}
                  </button>
                )}
              </div>
            </article>
          )}
        </section>

        {/* ── Lower grid: Category + Insights ── */}
        <section className="lower-grid reveal delay-3">
          {/* Category breakdown per week */}
          <article className="panel categories-panel" data-testid="categories-panel">
            <div className="panel-heading" style={{ marginBottom: "16px" }}>
              <div>
                <span className="panel-kicker">WHERE IT WENT</span>
                <h3>Kategori pengeluaran</h3>
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <button className="text-button" onClick={() => setShowAllCategories(s => !s)} data-testid="view-all-categories-button">
                  {showAllCategories ? "Ringkas" : "Lihat semua"} <ArrowUpRight size={15} />
                </button>
              </div>
            </div>

            {/* Week filter pills */}
            {isAuthenticated && weeklyBreakdown.length > 1 && (
              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "20px" }}>
                <button
                  onClick={() => setSelectedWeekFilter("all")}
                  style={{
                    font: "7px 'Press Start 2P'",
                    padding: "6px 10px",
                    border: "1px solid var(--line)",
                    background: selectedWeekFilter === "all" ? "var(--ink)" : "white",
                    color: selectedWeekFilter === "all" ? "white" : "var(--ink)",
                    cursor: "pointer",
                    boxShadow: selectedWeekFilter === "all" ? "2px 2px 0 var(--line)" : "none"
                  }}
                >
                  SEMUA MINGGU
                </button>
                {weeklyBreakdown.map(w => (
                  <button
                    key={w.week_start}
                    onClick={() => setSelectedWeekFilter(w.week_start)}
                    style={{
                      font: "7px 'Press Start 2P'",
                      padding: "6px 10px",
                      border: "1px solid var(--line)",
                      background: selectedWeekFilter === w.week_start ? "var(--ink)" : "white",
                      color: selectedWeekFilter === w.week_start ? "white" : "var(--ink)",
                      cursor: "pointer",
                      boxShadow: selectedWeekFilter === w.week_start ? "2px 2px 0 var(--line)" : "none"
                    }}
                  >
                    {w.formattedRange}
                  </button>
                ))}
              </div>
            )}

            {/* Weekly Grouped Sections */}
            <div className="category-list">
              {!isAuthenticated ? (
                <div style={{ color: "var(--muted)", fontSize: "13px", padding: "16px 0" }}>
                  <p style={{ margin: "0 0 14px", lineHeight: "1.5" }}>
                    Kategori pengeluaranmu bakal dikelompokkan per minggu di sini setelah kamu mencatat transaksi. Masuk atau daftar akun dulu ya.
                  </p>
                  <a href="/login" className="primary-button" style={{ textDecoration: "none", display: "inline-flex", width: "fit-content", fontSize: "8px", padding: "8px 12px" }}>
                    <Sparkles size={13} /> MASUK / DAFTAR
                  </a>
                </div>
              ) : filteredWeeks.length === 0 ? (
                <div style={{ color: "var(--muted)", fontSize: "13px", padding: "16px 0" }}>
                  Belum ada transaksi pengeluaran bulan ini.{" "}
                  <button className="text-button" onClick={() => setModal("transaction")}>Catat sekarang</button>
                </div>
              ) : (
                filteredWeeks.map((week) => {
                  const displayCats = showAllCategories ? week.categories : week.categories.slice(0, 4);
                  return (
                    <div key={week.week_start} style={{ marginBottom: "26px" }}>
                      {/* Week Partition Header */}
                      <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "8px 12px",
                        background: "#f4f1ea",
                        border: "1px solid #e2ded5",
                        marginBottom: "12px"
                      }}>
                        <span style={{ font: "8px 'Press Start 2P'", color: "var(--ink)" }}>
                           {week.formattedRange}
                        </span>
                        <b style={{ font: "700 13px 'Space Grotesk'", color: "var(--red)" }}>
                          Total: {formatRupiah(week.total_expense)}
                        </b>
                      </div>

                      {/* Categories in this week */}
                      {displayCats.map((cat) => (
                        <div className="category-row" key={`${week.week_start}-${cat.id || cat.name}`} data-testid={`category-${cat.name.toLowerCase().replaceAll(" ", "-")}`}>
                          <div className="category-icon">{cat.emoji}</div>
                          <div className="category-info">
                            <div className="category-name">
                              <span>{cat.name}</span>
                              <b>{formatRupiah(cat.total)}</b>
                            </div>
                            <div className="progress-track">
                              <span className={cat.tone} style={{ width: `${cat.percent}%` }} />
                            </div>
                            <span className={`category-tag ${cat.tone}`}>
                              {cat.percent}% dari total minggu ini
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })
              )}
            </div>
          </article>

          {/* Insights */}
          <article className="panel insight-panel" id="insights" data-testid="insights-panel">
            <div className="panel-heading">
              <div>
                <span className="panel-kicker">SMART TAKEAWAYS</span>
                <h3>Insight, bukan ceramah</h3>
              </div>
              <div className="insight-icon"><Lightbulb size={17} /></div>
            </div>

            {!isAuthenticated ? (
              <div style={{ color: "var(--muted)", fontSize: "13px", padding: "16px 0" }}>
                <p style={{ margin: "0 0 14px", lineHeight: "1.5" }}>
                  Insight cerdas & rekomendasi keuangan mingguan bakal otomatis diulas oleh AI setelah kamu login dan mencatat transaksi.
                </p>
                <a href="/login" className="primary-button" style={{ textDecoration: "none", display: "inline-flex", width: "fit-content", fontSize: "8px", padding: "8px 12px" }}>
                  <Sparkles size={13} /> MASUK / DAFTAR
                </a>
              </div>
            ) : insightsLoading ? (
              <div style={{ color: "var(--muted)", fontSize: "13px", padding: "16px 0" }}>Memuat insight...</div>
            ) : insights.length === 0 ? (
              <div style={{ color: "var(--muted)", fontSize: "13px", padding: "16px 0" }}>
                Generate roast dulu buat dapat insight minggu ini 🌶️
              </div>
            ) : (
              <>
                {insights.map((ins, i) => (
                  <div className="insight-item" key={ins.id}>
                    <span className="insight-number">{String(i + 1).padStart(2, "0")}</span>
                    <div>
                      <b>{ins.title}</b>
                      <p>{ins.description}</p>
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Roast history bottom */}
            {isAuthenticated && roastHistory.length > 0 && (
              <div className="recommendation" data-testid="saving-recommendation">
                <span></span>
                <div>
                  <b>ROAST HISTORY</b>
                  <p>
                    {roastHistory.length} roast tersimpan.{" "}
                    Gunakan navigasi minggu di atas untuk lihat roast lama.
                  </p>
                </div>
              </div>
            )}
          </article>
        </section>

        <footer className="footer">
          <span>FINANCIAL ROAST · {isAuthenticated ? (user?.username?.toUpperCase() || "USER") : "GUEST"}</span>
          <span data-testid="footer-disclaimer">
            <CircleHelp size={14} /> {isAuthenticated ? "Data real dari akunmu, bukan demo." : "Mode Tamu. Masuk untuk simpan data."}
          </span>
        </footer>
      </main>
    </div>
  );
}

/* ─── Root App with Router + Auth ─── */
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginGate />} />
          <Route path="/" element={<Home />} />
          <Route path="/*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

/* Redirect to dashboard if already logged in */
function LoginGate() {
  const { isAuthenticated } = useAuth();
  if (isAuthenticated) return <Navigate to="/" replace />;
  return <Login />;
}

export default App;
